import { useCallback, useEffect, useMemo } from 'react';
import type { StepInitStatus } from 'components/layout';
import { useLocationParams } from 'hooks';
import { buildDeepLinkFailedMessages } from '../utils/deepLinkInit';
import type { DeepLinkFailedStepMessage } from '../utils/deepLinkInit';
import type { DeepLinkInitializerProps } from './deepLinkTypes';
import useDeepLinkAdAccountsStep from './useDeepLinkAdAccountsStep';
import useDeepLinkAdSetsStep from './useDeepLinkAdSetsStep';
import useDeepLinkAdStep from './useDeepLinkAdStep';
import useDeepLinkCampaignsStep from './useDeepLinkCampaignsStep';
import useDeepLinkCreativesStep from './useDeepLinkCreativesStep';
import useDeepLinkFormRestore from './useDeepLinkFormRestore';

export interface UseDeepLinkInitializerResult {
  anyFailed: boolean;
  failedStepMessages: DeepLinkFailedStepMessage[];
  handleStartFromBeginning: () => void;
}

const useDeepLinkInitializer = ({
  targetStep,
  onComplete,
  onReset,
  onStatusChange,
}: DeepLinkInitializerProps): UseDeepLinkInitializerResult => {
  const { resetLocationParamsToStart } = useLocationParams();

  const creatives = useDeepLinkCreativesStep();
  const needsAccountCheck = targetStep >= 1 && creatives.pass;
  const needsCampaignCheck = targetStep >= 2 && creatives.pass;
  const needsAdSetCheck = targetStep >= 3 && creatives.pass;
  const needsAdCheck = targetStep >= 4 && creatives.pass;

  const accounts = useDeepLinkAdAccountsStep({ enabled: needsAccountCheck });
  const campaigns = useDeepLinkCampaignsStep({
    enabled: needsCampaignCheck,
    previousPass: accounts.pass,
  });
  const adSets = useDeepLinkAdSetsStep({
    enabled: needsAdSetCheck,
    previousPass: campaigns.pass,
  });
  const ad = useDeepLinkAdStep({
    enabled: needsAdCheck,
    previousPass: adSets.pass,
    restoredFlows: adSets.restoredFlows,
    restoredAdSetsForPartnershipSettings:
      adSets.restoredAdSetsForPartnershipSettings,
  });

  const allPass =
    creatives.pass && accounts.pass && campaigns.pass && adSets.pass && ad.pass;
  const anyFailed =
    creatives.status === 'failed' ||
    accounts.status === 'failed' ||
    campaigns.status === 'failed' ||
    adSets.status === 'failed' ||
    ad.status === 'failed';

  const statuses = useMemo<StepInitStatus[]>(
    () => [
      creatives.status,
      accounts.status,
      campaigns.status,
      adSets.status,
      ad.status,
    ],
    [
      accounts.status,
      ad.status,
      adSets.status,
      campaigns.status,
      creatives.status,
    ]
  );

  useEffect(() => {
    onStatusChange(statuses);
  }, [onStatusChange, statuses]);

  useDeepLinkFormRestore({
    allPass,
    restoreCreatives: creatives.restore,
    restoreCampaigns: campaigns.restore,
    restoreAdSets: adSets.restore,
    restoreAd: ad.restore,
    onComplete,
  });

  const failedStepMessages = useMemo(
    () =>
      buildDeepLinkFailedMessages({
        statuses,
        missingByStep: [
          creatives.missing,
          accounts.missing,
          campaigns.missing,
          adSets.missing,
          ad.missing,
        ],
        mismatchMessage: creatives.mismatchMessage,
      }),
    [
      accounts.missing,
      ad.missing,
      adSets.missing,
      campaigns.missing,
      creatives.missing,
      creatives.mismatchMessage,
      statuses,
    ]
  );

  const handleStartFromBeginning = useCallback(() => {
    resetLocationParamsToStart();
    onReset();
  }, [onReset, resetLocationParamsToStart]);

  return {
    anyFailed,
    failedStepMessages,
    handleStartFromBeginning,
  };
};

export default useDeepLinkInitializer;
